import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import ModalWrapper from './ModalWrapper';
import { FAILED_FAX_VISIBILITY_FEATURE, verifyFeatureAccess } from '../../global/featureFlags';
import './chartModal.scss';
import './resendRecordModal.scss';

// The server's 409 when the fax already has a newer resend (ENG-3679); any other 409
// means it is still being sent.
const ALREADY_RESENT_PREFIX = 'This fax was already resent';
const ALREADY_RESENT_MESSAGE = `${ALREADY_RESENT_PREFIX}. Resend the newer fax instead.`;

// Titles the server's 409 reason: already resent, still being sent (ENG-3340), or the
// fax's status changed under the page (a late provider callback).
const conflictTitle = (message) => {
  if (message.startsWith(ALREADY_RESENT_PREFIX)) return 'Already resent';
  if (message.toLowerCase().includes('still being sent')) return 'Still being sent';
  return 'Can\u2019t resend this fax';
};

class ResendRecord extends Component {
  constructor(props, context) {
    super(props, context);

    const params = this.props.modalType.data;
    this.state = {
      params,
      // ENG-3679: a fax the row already shows as resent opens straight on the reason,
      // the same answer the server would give, instead of asking to resend it.
      conflictMessage: params && params.recipient && params.recipient.resent === true ? ALREADY_RESENT_MESSAGE : null,
    };
  }

  /*
   * Resends records to the recipients. If the recipient is a fax number, it will
   * send a new access code page. If the recipient is a Medsender User, they will
   * simply receive a new email notification of record received.
   */
  resend = async () => {
    const {
      recipient, days, source, onResent,
    } = this.state.params;
    const { isBulkResend, resendRecord, bulkResend } = this.props;
    if (isBulkResend) {
      await bulkResend(days);
    } else {
      // ENG-3340: with the feature on, never replay a resend on 503, and explain a
      // 409 (still being sent) here instead of reporting success.
      const guarded = await verifyFeatureAccess(FAILED_FAX_VISIBILITY_FEATURE);
      const result = await resendRecord(recipient, guarded ? { retryOnUnavailable: false } : {});
      if (result && result.error && result.response && result.response.status === 409) {
        const serverMessage = result.response.data && result.response.data.error;
        this.setState({ conflictMessage: serverMessage || 'This fax is still being sent. Try again later.' });
        return;
      }
      if (source === 'status') {
        this.props.hideModal();
        if (result && result.error) return;
        if (onResent) {
          onResent(this.resentSendToken(recipient, result));
        } else {
          this.refreshStatusPage(recipient, result);
        }
        return;
      }
    }
    if (!this.props.error) {
      this.context.router.history.push('/app/sent');
    }
    this.props.hideModal();
  }

  // A resend from the status page stays there and reloads it; the resend can issue
  // a new send token, so follow it.
  resentSendToken = (recipient, result) => {
    const sentRow = result && result.data && result.data.sent_data && result.data.sent_data[0];
    return (sentRow && sentRow.send_token) || recipient.send_token;
  };

  refreshStatusPage = (recipient, result) => {
    const sendToken = this.resentSendToken(recipient, result);
    const statusPath = `/app/records/status/record/${recipient.file_token}/receiver/${sendToken}`;
    if (sendToken !== recipient.send_token) this.context.router.history.replace(statusPath);
    this.props.getStatusOfSentRecordById(recipient.file_token, sendToken);
  };

  recipientLabel = () => {
    const { recipient } = this.state.params;
    return recipient.receiver_name || recipient.receiver_email || `fax number ${recipient.receiver_fax}`;
  };

  // Title and one sentence of context for the dialog's current state.
  copy = () => {
    const { isBulkResend } = this.props;
    const { conflictMessage, params } = this.state;
    if (conflictMessage) {
      return {
        title: conflictTitle(conflictMessage),
        body: conflictMessage,
      };
    }
    if (isBulkResend) {
      return {
        title: 'Resend unopened records?',
        body: `Every record sent before today that has not been opened in the past ${params.days} days will be sent again.`,
      };
    }
    const isFax = Boolean(params.recipient.receiver_fax) && !params.recipient.receiver_email;
    return {
      title: isFax ? 'Resend this fax?' : 'Resend this record?',
      body: `It will be sent again to ${this.recipientLabel()}.`,
    };
  };

  // Same chrome as the redesigned Create Chart / Send to Chart modals (chart-modal-wrap):
  // title bar with close, one sentence, outlined Cancel and a purple primary action.
  render() {
    const { isLoading, isBulkResend, hideModal } = this.props;
    const { conflictMessage } = this.state;
    const { title, body } = this.copy();
    const actionName = isBulkResend ? 'Resend records' : 'Resend';
    let dismiss = conflictMessage ? 'Close' : 'Cancel';
    if (isLoading) dismiss = null;

    return (
      <ModalWrapper
        hideModal={hideModal}
        modalTitle={title}
        width="480px"
        customContentStyle={{}}
        wrapClassName="chart-modal-wrap resend-record-modal"
        dismiss={dismiss}
        closable={!isLoading}
        action={conflictMessage ? null : this.resend}
        actionName={conflictMessage ? null : actionName}
        isLoading={isLoading}
      >
        <p className="resend-record-modal__body" role={conflictMessage ? 'alert' : undefined}>
          {body}
        </p>
      </ModalWrapper>
    );
  }
}

ResendRecord.contextTypes = {
  router: PropTypes.object.isRequired,
};

ResendRecord.propTypes = {
  bulkResend: PropTypes.func.isRequired,
  isBulkResend: PropTypes.bool.isRequired,
  resendRecord: PropTypes.func.isRequired,
  hideModal: PropTypes.func.isRequired,
  modalType: PropTypes.object.isRequired,
  error: PropTypes.bool.isRequired,
  isLoading: PropTypes.bool.isRequired,
  getStatusOfSentRecordById: PropTypes.func,
};

ResendRecord.defaultProps = {
  getStatusOfSentRecordById: () => {},
};


export default connect(state => ({
  error: state.records.error,
  isLoading: state.records.isLoading,
}))(ResendRecord);
export { ResendRecord };
